import { useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  getFirstPaginatedNode,
  NavigationItem,
  SubmissionDetailData,
  useGetApplicationQuestionsQuery,
} from '@converge/graphql';
import { useConvergeNavigation } from '../../../contexts';
import { ApplicationContent } from '../../application';
import { UnderwritingSubmissionPageContainer } from '../underwriting.submission.page.container';
import { UnderwritingSubmissionDetailPageSlug } from '@converge/data';

interface IUnderwritingSubmissionDetailQuestionnaireProps {
  selectedLeftNavItem: NavigationItem;
  selectedSubLeftNavItem: NavigationItem | null;
  submission: SubmissionDetailData;
}

export const UnderwritingSubmissionDetailQuestionnaire = () => {
  const { selectedLeftNavItem, selectedSubLeftNavItem, submission } =
    useOutletContext<IUnderwritingSubmissionDetailQuestionnaireProps>();
  const navigate = useConvergeNavigation();

  const subNavItems = useMemo(
    () => selectedLeftNavItem?.subItems ?? [],
    [selectedLeftNavItem]
  );
  const subNavItemsIndex = useMemo(() => {
    const currentIndex = subNavItems.findIndex(
      (e) => e.value === selectedSubLeftNavItem?.value
    );

    return { currentIndex, itemLength: subNavItems.length };
  }, [subNavItems, selectedSubLeftNavItem]);

  const currentPosition = useMemo(() => {
    if (subNavItemsIndex.currentIndex === 0) {
      return 'first';
    } else if (
      subNavItemsIndex.currentIndex ===
      subNavItemsIndex.itemLength - 1
    ) {
      return 'last';
    } else {
      return 'middle';
    }
  }, [subNavItemsIndex]);

  const application = useMemo(
    () => getFirstPaginatedNode(submission?.applications),
    [submission?.applications]
  );

  const { data: nestedQuestion, loading } = useGetApplicationQuestionsQuery({
    submissionApplicationUuid: application?.submissionApplication.node?.uuid,
  });
  if (!application) {
    return null;
  }

  const onNextClick = () => {
    if (currentPosition === 'last') {
      navigate.underwritingSubmissionDetail({
        submissionUuid: submission.uuid,
        applicationUuid: application.uuid,
        // TODO: Remove the fallback after API ticket CON-1589 is done (https://convergeins.atlassian.net/browse/CON-1589)
        submissionApplicationUuid: application.submissionApplication.node?.uuid,
        sectionUrl: 'risk-profile',
      });
    } else {
      const nextItem = subNavItems[subNavItemsIndex.currentIndex + 1];

      navigate.underwritingSubmissionDetail({
        submissionUuid: submission.uuid,
        applicationUuid: application.uuid,
        // TODO: Remove the fallback after API ticket CON-1589 is done (https://convergeins.atlassian.net/browse/CON-1589)
        submissionApplicationUuid: application.submissionApplication.node?.uuid,
        sectionUrl: selectedLeftNavItem.value,
        subSectionUrl: nextItem.value,
      });
    }
  };

  const onPreviousClick = () => {
    if (subNavItemsIndex.currentIndex === 0) {
      navigate.underwritingSubmissionDetail({
        submissionUuid: submission.uuid,
        sectionUrl: UnderwritingSubmissionDetailPageSlug.Overview,
      });
      return;
    }
    const previousItem = subNavItems[subNavItemsIndex.currentIndex - 1];

    navigate.underwritingSubmissionDetail({
      submissionUuid: submission.uuid,
      applicationUuid: application.uuid,
      // TODO: Remove the fallback after API ticket CON-1589 is done (https://convergeins.atlassian.net/browse/CON-1589)
      submissionApplicationUuid: application.submissionApplication.node?.uuid,
      sectionUrl: selectedLeftNavItem.value,
      subSectionUrl: previousItem.value,
    });
  };
  return nestedQuestion ? (
    <UnderwritingSubmissionPageContainer title="Questionnaire">
      <ApplicationContent
        selectedAppSection={selectedSubLeftNavItem}
        application={application}
        nestedQuestions={nestedQuestion?.nestedQuestions.data || []}
        questionsLoading={loading}
        onNextClick={onNextClick}
        onPreviousClick={onPreviousClick}
        position={currentPosition}
        submissionUuid={submission?.uuid}
        usePrompt={() => {}}
      />
    </UnderwritingSubmissionPageContainer>
  ) : (
    <></>
  );
};
